import {
  ButtonProps as BaseButtonProps,
  Box,
  Button as ChakraButton,
  ResponsiveValue,
  forwardRef,
  useStyleConfig,
  useToken,
} from "@chakra-ui/react"
import { ButtonDS } from "DesignSystem/chakra-theme/components/ButtonDS"
import { isArray, isObject, mapValues } from "lodash"
import React from "react"
import { SPINNER_SIZES, Spinner } from "./Spinner"

type IsSelectedProp = {
  isSelected?: boolean
  allowWrapping?: boolean
}

type ButtonVariant = keyof NonNullable<typeof ButtonDS.variants>
type ButtonSize = keyof NonNullable<typeof ButtonDS.sizes>

export type ButtonProps = Omit<BaseButtonProps, "size" | "variant"> &
  IsSelectedProp & {
    variant?: ResponsiveValue<ButtonVariant>
    size?: ResponsiveValue<ButtonSize>
  }

/**
 * - Use `variant` and `size` props to control appearance.
 * - Use the `isSelected` prop to access the selected state.
 *   - State management for selected buttons is handled by the consumer (similar to `isDisabled`).
 * - For buttons that are links under the hood, use a React Router link (so that
 * it navigates within the SPA rather than loading a new page) by doing
 * `as={ReactRouterLink} to={ROUTES.foo}`.
 *
 * @example
 * // Default variant and default size
 * <Button>Go</Button>
 * // Specific variant and size
 * <Button variant="primary" size="emphasized">Go</Button>
 * // Link that looks like a button
 * <Button as={ReactRouterLink} to={ROUTES.foo} variant="primary" size="emphasized">Go</Button>
 */
export const Button = forwardRef<ButtonProps, "button">(
  (
    { sx = {}, children, isDisabled, isLoading, ...props }: ButtonProps,
    ref
  ) => {
    const { variant, size, isSelected, allowWrapping, ...rest } = props

    const styles = useStyleConfig("ButtonDS", { variant, size })
    const stylesWhenSelected = useSelectedStyles()

    const styleOverrides = isSelected ? stylesWhenSelected : undefined

    return (
      <ChakraButton
        ref={ref}
        position="relative"
        aria-selected={isSelected || undefined}
        isDisabled={isDisabled || isLoading}
        data-loading={isLoading || undefined}
        {...rest}
        __css={styles}
        whiteSpace={allowWrapping ? undefined : "nowrap"}
        sx={{ ...sx, ...styleOverrides }}
      >
        {children}
        {isLoading && (
          <Box position="absolute" inset={0} display="grid" placeItems="center">
            <Spinner size={spinnerSize(size ?? "default")} />
          </Box>
        )}
      </ChakraButton>
    )
  }
)

// These are also used in the IconButton
export const useSelectedStyles = () => {
  const selectedBorderColor = useToken(
    "ds.border.selected",
    "var(--chakra-colors-ds-border-selected)"
  )
  return {
    bg: "ds.background.selected.subtle.resting !important",
    color: "ds.text.selected",
    // boxShadow because we want an inside border, so selected buttons don't push their siblings around
    boxShadow: `inset 0px 0px 0px 1px ${selectedBorderColor}`,
    _hover: {
      bg: "ds.background.selected.subtle.resting",
      color: "ds.text.selected",
    },
    "& svg": { color: "ds.icon.selected" },
  }
}

const BUTTON_SPINNER_SIZES = {
  default: "medium",
  emphasized: "medium",
  compact: "small",
  flush: "xsmall",
} as const

export const spinnerSize = (
  size: ResponsiveValue<ButtonSize>
): ResponsiveValue<keyof typeof SPINNER_SIZES> => {
  if (isArray(size)) {
    return size.map((s) => BUTTON_SPINNER_SIZES[s ?? "default"])
  }

  if (isObject(size)) {
    return mapValues(size, (s) => BUTTON_SPINNER_SIZES[s ?? "default"])
  }

  return BUTTON_SPINNER_SIZES[size]
}

Button.displayName = "Button"
